// ─────────────────────────────────────────────────────────────────────────────
// Term timeline classification — Course Evaluations Dashboard
// ─────────────────────────────────────────────────────────────────────────────

export type TermTimelinePosition =
  | "past"
  | "last"
  | "current"
  | "upcoming"
  | "future"

const MS_PER_DAY = 86_400_000

function dayDiff(from: Date, to: Date): number {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate())
  return Math.round((a - b) / MS_PER_DAY)
}

/**
 * Classify a term relative to today using the boundary rules from the product spec.
 * Current: S ≤ T ≤ E + 14
 * Last: 14 < (T − E) ≤ 30
 * Past: T − E > 30
 * Upcoming: 0 < (S − T) ≤ 30
 * Future: S − T > 30
 */
export function classifyTermTimelinePosition(
  today: Date,
  startDate: Date,
  endDate: Date,
): TermTimelinePosition {
  const daysAfterEnd = dayDiff(today, endDate)
  const daysBeforeStart = dayDiff(startDate, today)

  if (daysBeforeStart > 30) return "future"
  if (daysBeforeStart > 0) return "upcoming"
  if (daysAfterEnd <= 14) return "current"
  if (daysAfterEnd <= 30) return "last"
  return "past"
}

export function formatTermDateRange(start: string, end: string): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  return `${fmt(start)} to ${fmt(end)}`
}

export function daysUntil(isoDate: string, from = new Date()): number {
  return dayDiff(new Date(isoDate), from)
}

export function daysSince(isoDate: string, from = new Date()): number {
  return dayDiff(from, new Date(isoDate))
}

/** Product-facing timeline chip (Live · Last · Past · Upcoming · Future). */
export type TermTimelineStatusLabel = "Live" | "Last" | "Past" | "Upcoming" | "Future"

export function termTimelineStatusMeta(position: TermTimelinePosition): {
  label: TermTimelineStatusLabel
  tone: "success" | "info" | "neutral" | "warning"
  icon: string
} {
  switch (position) {
    case "current":
      return { label: "Live", tone: "success", icon: "fa-signal-stream" }
    case "last":
      return { label: "Last", tone: "warning", icon: "fa-clock" }
    case "upcoming":
      return { label: "Upcoming", tone: "info", icon: "fa-calendar-plus" }
    case "future":
      return { label: "Future", tone: "neutral", icon: "fa-calendar" }
    case "past":
    default:
      return { label: "Past", tone: "neutral", icon: "fa-calendar-check" }
  }
}
